import { TeamId } from "@custom-types/base-types";
import { SdbTeam } from "@custom-types/teams-types";
import { createSelector } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { companyTeamsAsMemberSelector } from "@store/members/members-selector";
import { RootState } from "@store/store-helper";
import { teamAdapter } from "@store/teams/teams-slice";
import { TeamState } from "@store/teams/teams-slice-types";

/** Return the list of teams. */
export const teamsSelector: (state: RootState) => SdbTeam[] = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const teams = teamAdapter.getSelectors().selectAll(state.teams);
    const companyTeamsAsMember = companyTeamsAsMemberSelector(state);
    const searchText = state.ui.search.debouncedSearchText.toLowerCase();
    const sdbTeams: SdbTeam[] = teams
      .filter((team) => team.name.toLowerCase().includes(searchText))
      .map((team) => {
        const teamAsMember = companyTeamsAsMember.find(
          (teamAsMember) => teamAsMember.identity === team.id
        );
        return {
          ...team,
          role: teamAsMember?.role ?? "MEMBER",
        };
      });

    return sdbTeams.sort(
      (teamA, teamB) =>
        new Date(teamB.modifiedAt).getTime() -
        new Date(teamA.modifiedAt).getTime()
    );
  }
);

/**
 * Gets a team entity by providing the team ID
 */
export function getTeamByIdSelector(
  teamId?: TeamId
): (state: RootState) => FaroCoreApiTypes.Team | undefined {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      if (!teamId) {
        return undefined;
      }
      return teamAdapter.getSelectors().selectById(state.teams, teamId);
    }
  );
}

/**
 * Get the selected team using the selected team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store.
 */
export const selectedTeamIdSelector: (
  state: RootState
) => FaroCoreApiTypes.Team | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const selectedTeamId = state.teams.selectedTeam.id;
    if (!selectedTeamId) {
      return null;
    }

    return (
      teamAdapter.getSelectors().selectById(state.teams, selectedTeamId) ?? null
    );
  }
);

/**
 * Get the selected team using the selected team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store,
 * including the user's role in the team if available.
 */
export const selectedTeamWithRoleSelector: (
  state: RootState
) => SdbTeam | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const selectedTeamId = state.teams.selectedTeam.id;
    if (!selectedTeamId) {
      return null;
    }

    const team =
      teamAdapter.getSelectors().selectById(state.teams, selectedTeamId) ??
      null;

    if (!team) {
      return null;
    }

    const companyTeamsAsMember = companyTeamsAsMemberSelector(state);
    const teamAsMember = companyTeamsAsMember.find(
      (teamAsMember) => teamAsMember.identity === team.id
    );

    return {
      ...team,
      role: teamAsMember?.role ?? "MEMBER",
    };
  }
);

/**
 * Get the team using the team ID and finding it on the store,
 * returns null if there's no teamId or if the team could not be found in the store,
 * including the user's role in the team if available.
 */
export function teamWithRoleByIdSelector(
  teamId?: TeamId
): (state: RootState) => SdbTeam | null {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      if (!teamId) {
        return null;
      }

      const team =
        teamAdapter.getSelectors().selectById(state.teams, teamId) ?? null;

      if (!team) {
        return null;
      }

      const companyTeamsAsMember = companyTeamsAsMemberSelector(state);
      const teamAsMember = companyTeamsAsMember.find(
        (teamAsMember) => teamAsMember.identity === team.id
      );

      return {
        ...team,
        role: teamAsMember?.role ?? "MEMBER",
      };
    }
  );
}

/**
 * Returns the fetching properties of the teams slice.
 */
export const fetchingTeamsFlagsSelector: (
  state: RootState
) => TeamState["fetching"] = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return state.teams.fetching;
  }
);

/**
 * Returns the members of the selected team.
 */
export const selectedTeamMembersSelector: (
  state: RootState
) => FaroCoreApiTypes.TeamMember[] = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return Object.values(state.teams.selectedTeam.members);
  }
);

/** Returns the selected team as member type */
export const selectedTeamAsMemberSelector: (
  state: RootState
) => FaroCoreApiTypes.CompanyMemberDetails | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return state.teams.selectedTeam.teamAsMember;
  }
);

/**
 * Returns the next team ID if set.
 */
export const nextTeamSelector: (state: RootState) => string | null =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return state.teams.nextTeam;
    }
  );

/**
 * Returns the next team member ID if set.
 */
export const nextTeamMemberSelector: (state: RootState) => string | null =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return state.teams.nextTeamMember;
    }
  );

/**
 * Returns whether the details panel is opened.
 */
export const isDetailOpenedSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return state.teams.isDetailOpened;
    }
  );
